import React, { useCallback, useReducer, useState } from "react"
import type { ErroApi } from "../../../services/emissoesParaMeuCNPJ"
import { baixarZipNotas, buscarEmissoes } from "../../../services/emissoesParaMeuCNPJ"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { EmissoesParaMeuCNPJInicial } from "../models/emissoesParaMeuCNPJModel"
import { EmissoesParaMeuCNPJActionsTypes } from "./emissoesParaMeuCNPJActions"
import { EmissoesParaMeuCNPJContext } from "./EmissoesParaMeuCNPJContext"
import { emissoesParaMeuCNPJReducer } from "./emissoesParaMeuCNPJReducer"

function triggerDownload(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

function isErroApi(v: unknown): v is ErroApi {
    return typeof v === "object" && v !== null && "retornoErro" in v
}

export function EmissoesParaMeuCNPJContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(emissoesParaMeuCNPJReducer, EmissoesParaMeuCNPJInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const [baixandoZip, setBaixandoZip] = useState(false)
    const setLoading = useLoadingStore(s => s.setLoading)

    const buscar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await buscarEmissoes(autorizacao, dados.dataInicial, dados.dataFinal)
            if (isErroApi(response)) {
                setErroCarregamento(response.mensagem || "Erro ao carregar")
            } else {
                setErroCarregamento(null)
                dispatch({ type: EmissoesParaMeuCNPJActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar")
        } finally {
            setLoading(false)
        }
    }, [dados.dataInicial, dados.dataFinal, setLoading])

    const handleBaixarZip = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setBaixandoZip(true)
            const result = await baixarZipNotas(autorizacao, dados.dataInicial, dados.dataFinal)
            if (isErroApi(result)) {
                setErroCarregamento(result.mensagem || "Erro ao gerar ZIP")
            } else {
                triggerDownload(result, `notas_${dados.dataInicial}_${dados.dataFinal}.zip`)
            }
        } catch {
            setErroCarregamento("Erro ao gerar ZIP")
        } finally {
            setBaixandoZip(false)
        }
    }, [dados.dataInicial, dados.dataFinal])

    return (
        <EmissoesParaMeuCNPJContext.Provider
            value={{ dados, dispatch, erroCarregamento, buscar, baixandoZip, handleBaixarZip }}
        >
            <Loading />
            {children}
        </EmissoesParaMeuCNPJContext.Provider>
    )
}
